// Damage Assessment Types

import type { BadgeVariant } from '@/components/common/Badge';

export type DamageAssessmentStatus =
  | 'draft'
  /** Submitted from the field while photos were still uploading (P2-02); becomes pending_review on its own. */
  | 'submit_pending_attachments'
  | 'pending_review'
  | 'in_review'
  | 'verified'
  | 'approved'
  | 'rejected'
  | 'archived';

export type DamageSeverity = 'affected' | 'minor' | 'major' | 'destroyed';

export type StructureType =
  | 'single_family'
  | 'multi_family'
  | 'mobile_home'
  | 'apartment'
  | 'commercial'
  | 'industrial'
  | 'agricultural'
  | 'government'
  | 'infrastructure'
  | 'utility'
  | 'other';

export type OwnershipType =
  | 'owner_occupied'
  | 'renter_occupied'
  | 'vacant'
  | 'commercial'
  | 'government'
  | 'unknown';

export interface GeoPoint {
  type: 'Point';
  coordinates: [number, number]; // [lng, lat]
}

export interface GeoPolygon {
  type: 'Polygon';
  coordinates: number[][][];
}

export interface PhotoAttachment {
  id: string;
  filename: string;
  mime_type: string;
  size: number;
  storage_key?: string;
  exif_data?: ExifData;
  uploaded_at: string;
  uploaded_by: string;
  caption?: string;
  thumbnail_url?: string;
  url?: string;
  // For local/offline photos
  local_uri?: string;
}

export interface ExifData {
  date_taken?: string;
  gps_lat?: number;
  gps_lng?: number;
  camera_make?: string;
  camera_model?: string;
  orientation?: number;
}

export interface DamageAssessment {
  id: string;
  tenant_id: string;
  incident_id?: string;
  assessment_number: string;

  // Location
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  county?: string;
  location_point?: GeoPoint;
  affected_area?: GeoPolygon;

  // Structure details
  structure_type: StructureType;
  ownership_type?: OwnershipType;

  // Damage assessment
  severity: DamageSeverity;

  // Damage categories
  foundation_damage: boolean;
  roof_damage: boolean;
  wall_damage: boolean;
  window_damage: boolean;
  electrical_damage: boolean;
  plumbing_damage: boolean;
  hvac_damage: boolean;
  flood_damage: boolean;
  fire_damage: boolean;

  // Habitability
  is_habitable: boolean;
  occupants_displaced: number;

  // Cost estimates
  estimated_damage_cost?: number;
  insurance_coverage?: boolean;
  fema_assistance_eligible?: boolean;

  // Notes
  damage_description?: string;
  notes?: string;

  // Attachments
  attachments: PhotoAttachment[];

  // Status
  status: DamageAssessmentStatus;

  // Assignment
  assigned_to?: string;
  assessed_by?: string;
  assessed_at?: string;
  verified_by?: string;
  verified_at?: string;
  approved_by?: string;
  approved_at?: string;

  // Offline sync
  offline_id?: string;
  synced_at?: string;
  client_created_at?: string;

  // Metadata
  metadata: Record<string, unknown>;

  // Audit
  created_by: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;

  // Joined fields
  incident_name?: string;
  incident_number?: string;
  assigned_to_name?: string;
  assessed_by_name?: string;
  verified_by_name?: string;
  approved_by_name?: string;
  created_by_name?: string;
}

export interface CreateDamageAssessmentData {
  incident_id?: string;
  zone_id?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  county?: string;
  location_point?: GeoPoint;
  affected_area?: GeoPolygon;
  structure_type: StructureType;
  ownership_type?: OwnershipType;
  severity: DamageSeverity;
  foundation_damage?: boolean;
  roof_damage?: boolean;
  wall_damage?: boolean;
  window_damage?: boolean;
  electrical_damage?: boolean;
  plumbing_damage?: boolean;
  hvac_damage?: boolean;
  flood_damage?: boolean;
  fire_damage?: boolean;
  is_habitable?: boolean;
  occupants_displaced?: number;
  estimated_damage_cost?: number;
  insurance_coverage?: boolean;
  fema_assistance_eligible?: boolean;
  damage_description?: string;
  notes?: string;
  attachments?: PhotoAttachment[];
  assigned_to?: string;
  metadata?: Record<string, unknown>;
  offline_id?: string;
  client_created_at?: string;
}

export interface DamageAssessmentHistory {
  id: string;
  assessment_id: string;
  from_status?: DamageAssessmentStatus;
  to_status: DamageAssessmentStatus;
  changed_by: string;
  changed_by_name?: string;
  changed_at: string;
  /** P2-04 field time: effective time (device time bounded by the server's skew rules). */
  occurred_at?: string;
  /** The device clock as sent; null for console actions. */
  device_occurred_at?: string | null;
  received_at?: string;
  /** The device clock was outside the accepted bounds. */
  clock_suspect?: boolean;
  reason?: string;
  notes?: string;
}

export interface DamageAssessmentStats {
  total: number;
  by_status: Record<DamageAssessmentStatus, number>;
  by_severity: Record<DamageSeverity, number>;
  by_structure_type: Record<StructureType, number>;
  total_displaced: number;
  total_estimated_cost: number;
  habitable_count: number;
  uninhabitable_count: number;
}

// UI Configuration
// `variant` drives the shared Badge component for status/severity pills.
// The raw color fields are kept for non-pill consumers (map symbols, tints).
export const SEVERITY_CONFIG: Record<DamageSeverity, { label: string; color: string; bgColor: string; textColor: string; variant: BadgeVariant }> = {
  affected: {
    label: 'Affected',
    color: 'yellow',
    bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
    textColor: 'text-yellow-800 dark:text-yellow-300',
    variant: 'warning',
  },
  minor: {
    label: 'Minor',
    color: 'orange',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    textColor: 'text-orange-800 dark:text-orange-300',
    variant: 'orange',
  },
  major: {
    label: 'Major',
    color: 'red',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    textColor: 'text-red-800 dark:text-red-300',
    variant: 'danger',
  },
  destroyed: {
    label: 'Destroyed',
    color: 'purple',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    textColor: 'text-purple-800 dark:text-purple-300',
    variant: 'purple',
  },
};

export const STATUS_CONFIG: Record<DamageAssessmentStatus, { label: string; color: string; bgColor: string; textColor: string; variant: BadgeVariant }> = {
  draft: {
    label: 'Draft',
    color: 'gray',
    bgColor: 'bg-gray-100 dark:bg-gray-700',
    textColor: 'text-gray-800 dark:text-gray-300',
    variant: 'neutral',
  },
  submit_pending_attachments: {
    label: 'Submitted (photos uploading)',
    color: 'blue',
    bgColor: 'bg-primary-100 dark:bg-primary-900/30',
    textColor: 'text-primary-800 dark:text-primary-300',
    variant: 'info',
  },
  pending_review: {
    label: 'Pending Review',
    color: 'blue',
    bgColor: 'bg-primary-100 dark:bg-primary-900/30',
    textColor: 'text-primary-800 dark:text-primary-300',
    variant: 'info',
  },
  in_review: {
    label: 'In Review',
    color: 'yellow',
    bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
    textColor: 'text-yellow-800 dark:text-yellow-300',
    variant: 'warning',
  },
  verified: {
    label: 'Verified',
    color: 'cyan',
    bgColor: 'bg-cyan-100 dark:bg-cyan-900/30',
    textColor: 'text-cyan-800 dark:text-cyan-300',
    variant: 'sky',
  },
  approved: {
    label: 'Approved',
    color: 'green',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    textColor: 'text-green-800 dark:text-green-300',
    variant: 'success',
  },
  rejected: {
    label: 'Rejected',
    color: 'red',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    textColor: 'text-red-800 dark:text-red-300',
    variant: 'danger',
  },
  archived: {
    label: 'Archived',
    color: 'gray',
    bgColor: 'bg-gray-100 dark:bg-gray-700',
    textColor: 'text-gray-600 dark:text-gray-400',
    variant: 'neutral',
  },
};

export const STRUCTURE_TYPE_LABELS: Record<StructureType, string> = {
  single_family: 'Single Family',
  multi_family: 'Multi-Family',
  mobile_home: 'Mobile Home',
  apartment: 'Apartment',
  commercial: 'Commercial',
  industrial: 'Industrial',
  agricultural: 'Agricultural',
  government: 'Government',
  infrastructure: 'Infrastructure',
  utility: 'Utility',
  other: 'Other',
};

export const OWNERSHIP_TYPE_LABELS: Record<OwnershipType, string> = {
  owner_occupied: 'Owner Occupied',
  renter_occupied: 'Renter Occupied',
  vacant: 'Vacant',
  commercial: 'Commercial',
  government: 'Government',
  unknown: 'Unknown',
};

export const DAMAGE_CATEGORIES = [
  { key: 'foundation_damage', label: 'Foundation' },
  { key: 'roof_damage', label: 'Roof' },
  { key: 'wall_damage', label: 'Walls' },
  { key: 'window_damage', label: 'Windows' },
  { key: 'electrical_damage', label: 'Electrical' },
  { key: 'plumbing_damage', label: 'Plumbing' },
  { key: 'hvac_damage', label: 'HVAC' },
  { key: 'flood_damage', label: 'Flood' },
  { key: 'fire_damage', label: 'Fire' },
] as const;
